import api from './api';

export interface AuthUser {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  profileImageDataUrl?: string;
  emailVerified: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface AuthResponse {
  message: string;
  token: string;
  user: AuthUser;
}

export interface RegisterResponse {
  message: string;
  requiresVerification: boolean;
  email: string;
  verificationUrl?: string;
}

export interface VerificationResponse {
  message: string;
  user?: AuthUser;
  verificationUrl?: string;
}

export interface UpdateProfileRequest {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  profileImageDataUrl?: string;
}

const TOKEN_KEY = '@jamsession_token';
const USER_KEY = '@jamsession_user';

export class AuthService {
  // Register user
  async register(userData: RegisterRequest): Promise<RegisterResponse> {
    try {
      const response = await api.post('/api/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Login user
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await api.post('/api/auth/login', credentials);
      const authData = response.data;

      // Store token and user data
      await this.storeAuthData(authData.token, authData.user);

      return authData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  // Get current user
  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const userJson = localStorage.getItem(USER_KEY);
      return userJson ? JSON.parse(userJson) : null;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  // Get stored token
  async getToken(): Promise<string | null> {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (error) {
      console.error('Get token error:', error);
      return null;
    }
  }

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken();
    return !!token;
  }

  // Store authentication data
  private async storeAuthData(token: string, user: AuthUser): Promise<void> {
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('Store auth data error:', error);
      throw error;
    }
  }

  // Refresh user data from server
  async refreshUser(): Promise<AuthUser> {
    try {
      const token = await this.getToken();
      if (!token) {
        throw new Error('No token found');
      }

      const response = await api.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const user = response.data.user;
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      return user;
    } catch (error) {
      console.error('Refresh user error:', error);
      throw error;
    }
  }

  async updateCurrentUser(profile: UpdateProfileRequest): Promise<AuthUser> {
    try {
      const token = await this.getToken();
      if (!token) {
        throw new Error('No token found');
      }

      const response = await api.put('/api/auth/me', profile, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const user = response.data.user;
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      return user;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  async verifyEmail(token: string): Promise<VerificationResponse> {
    try {
      const response = await api.post('/api/auth/verify-email', { token });
      return response.data;
    } catch (error) {
      console.error('Verify email error:', error);
      throw error;
    }
  }

  async resendVerification(email: string): Promise<VerificationResponse> {
    try {
      const response = await api.post('/api/auth/resend-verification', {
        email,
      });
      return response.data;
    } catch (error) {
      console.error('Resend verification error:', error);
      throw error;
    }
  }
}

const authService = new AuthService();
export default authService;
